import { useEffect, useState } from "preact/hooks";
import { moveReviewId } from "../lib/view-model.js";
import type { Analysis, MoveLayout, MovePreviewLayout, Point } from "../types.js";

function midpoint(
  first: HTMLElement,
  last: HTMLElement,
  stageBox: DOMRect,
  edge: "left" | "right",
): Point {
  const a = first.getBoundingClientRect();
  const b = last.getBoundingClientRect();
  const pane = first.closest<HTMLElement>(".file-pane");
  const paneBox = pane?.getBoundingClientRect();
  return {
    x: (paneBox ? edge === "right" ? paneBox.right : paneBox.left : edge === "right" ? a.right : a.left) - stageBox.left,
    y: (a.top + b.bottom) / 2 - stageBox.top,
  };
}

export function useMoveLayout(
  analysis: Analysis,
  reviewed: Set<string>,
  showReviewed: boolean,
  hideUnchangedMoves: boolean,
): MoveLayout {
  const [layout, setLayout] = useState<MoveLayout>({ arrows: [], height: 0, previews: [], width: 0 });

  useEffect(() => {
    let frame = 0;
    let settleTimer = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const stage = document.getElementById("diff-stage");
        if (!stage) return;
        const stageBox = stage.getBoundingClientRect();
        const arrows: MoveLayout["arrows"] = [];
        const previews: MovePreviewLayout[] = [];

        analysis.moves.forEach((move) => {
          if (reviewed.has(moveReviewId(move.id)) && !showReviewed) return;
          const sourceId = move.source.lineIds[0];
          const targetId = move.target.lineIds[0];
          if (!sourceId || !targetId) return;
          const source = document.getElementById(sourceId);
          const target = document.getElementById(targetId);
          if (!source || !target) return;
          const sourceLast = document.getElementById(move.source.lineIds.at(-1) ?? sourceId) || source;
          const targetLast = document.getElementById(move.target.lineIds.at(-1) ?? targetId) || target;
          const from = midpoint(source, sourceLast, stageBox, "right");
          const to = midpoint(target, targetLast, stageBox, "left");
          const bend = Math.max(36, Math.min(110, Math.abs(to.y - from.y) * 0.16));
          arrows.push({
            bend,
            from,
            move,
            to,
          });

          const sourceCard = source.closest<HTMLElement>(".file-card");
          const targetCard = target.closest<HTMLElement>(".file-card");
          const sourcePane = source.closest<HTMLElement>(".file-pane");
          const targetPane = target.closest<HTMLElement>(".file-pane");
          if (!sourceCard || !targetCard || !sourcePane || !targetPane) return;
          const sourceBox = source.getBoundingClientRect();
          const targetBox = target.getBoundingClientRect();
          const sourceCardBox = sourceCard.getBoundingClientRect();
          const targetCardBox = targetCard.getBoundingClientRect();
          const sourcePaneBox = sourcePane.getBoundingClientRect();
          const targetPaneBox = targetPane.getBoundingClientRect();
          previews.push({
            container: sourceCard,
            left: sourcePaneBox.right - sourceCardBox.left - sourceCard.clientLeft,
            move,
            role: "target",
            top: sourceBox.top - sourceCardBox.top - sourceCard.clientTop + sourceCard.scrollTop,
            width: sourcePaneBox.width,
          });
          previews.push({
            container: targetCard,
            left: targetPaneBox.left - targetCardBox.left - targetCard.clientLeft - targetPaneBox.width,
            move,
            role: "source",
            top: targetBox.top - targetCardBox.top - targetCard.clientTop + targetCard.scrollTop,
            width: targetPaneBox.width,
          });
        });

        setLayout({
          arrows,
          height: stage.scrollHeight,
          previews,
          width: stage.scrollWidth,
        });
      });
    };
    const measureAfterScroll = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(measure, 140);
    };

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measureAfterScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measureAfterScroll);
    };
  }, [analysis, reviewed, showReviewed, hideUnchangedMoves]);

  return layout;
}

export function useSynchronizedPaneScrolling(layout: MoveLayout): void {
  useEffect(() => {
    const cleanups: Array<() => void> = [];
    document.querySelectorAll<HTMLElement>(".file-card").forEach((fileCard) => {
      const oldPane = fileCard.querySelector<HTMLElement>(".file-pane-old .file-pane-scroll");
      const newPane = fileCard.querySelector<HTMLElement>(".file-pane-new .file-pane-scroll");
      if (!oldPane || !newPane) return;
      let mirroredPane: HTMLElement | null = null;
      const synchronize = (source: HTMLElement, target: HTMLElement) => {
        if (mirroredPane === source) {
          mirroredPane = null;
          return;
        }
        if (target.scrollLeft === source.scrollLeft) return;
        mirroredPane = target;
        target.scrollLeft = source.scrollLeft;
      };
      const syncOld = () => synchronize(oldPane, newPane);
      const syncNew = () => synchronize(newPane, oldPane);
      oldPane.addEventListener("scroll", syncOld, { passive: true });
      newPane.addEventListener("scroll", syncNew, { passive: true });
      cleanups.push(() => {
        oldPane.removeEventListener("scroll", syncOld);
        newPane.removeEventListener("scroll", syncNew);
      });
    });
    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [layout.previews.length]);
}
